import { toast } from "sonner";
import HttpService from "@/hooks/Https-services";
import { APIRoutes, INSIDER_PLAN_ID } from "@/utils/constants";

/**
 * Mirrors src/Pages/PersonalChannel/MatchCardv2's per-clip download flow so
 * the v2 clip list and the "now playing" panel share one implementation
 * instead of two copies drifting apart.
 */
export type DownloadableClip = {
  game_event_id: string | number;
  duration?: string | number;
  away_team_name?: string;
  home_team_name?: string;
  game_date?: string;
  event_type?: string;
};

export type DownloadRecord = {
  ClipID: string | number;
  Status: string;
  DownloadUrl?: string;
  TaskID?: string;
};

export function findDownloadRecord(
  clip: DownloadableClip,
  allDownloadVideo: DownloadRecord[],
): DownloadRecord | null {
  return (
    allDownloadVideo?.find(
      (record) => String(record?.ClipID) === String(clip.game_event_id),
    ) ?? null
  );
}

export function canDownload(
  subscriptionDetails: any,
  userDetails: any,
): boolean {
  return (
    subscriptionDetails?.plan_type_id == INSIDER_PLAN_ID ||
    userDetails?.UserRole?.Permissions?.includes("download")
  );
}

/**
 * Returns true when a new download was accepted by the API, false when the
 * request failed. Callers use it to decide whether to refresh the download
 * records (and so leave the button disabled). A COMPLETE record is just a
 * file save, not a new request, so it returns false too.
 */
export async function requestClipDownload({
  clip,
  record,
  setIsDownloadAll,
}: {
  clip: DownloadableClip;
  record: DownloadRecord | null;
  setIsDownloadAll: (value: boolean) => void;
}): Promise<boolean> {
  if (record?.Status === "COMPLETE" && record?.DownloadUrl) {
    const a = document.createElement("a");
    a.href = record.DownloadUrl;
    a.target = "_blank";
    a.download = `${record.TaskID}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return false;
  }

  try {
    setIsDownloadAll(true);
    await HttpService.post(APIRoutes.DownloadClipsV2, {
      item: {
        duration: clip.duration,
        id: parseInt(String(clip.game_event_id)),
      },
      game_details: {
        away_team: clip.away_team_name,
        game_date_time: clip.game_date ? new Date(clip.game_date).toISOString() : undefined,
        home_team: clip.home_team_name,
      },
      other_details: {
        clip_id: clip.game_event_id,
        type: clip.event_type?.split("_")?.join(" "),
      },
    });
    toast.info(
      "Your download has been requested. You will receive an email when it is complete.",
    );
    return true;
  } catch (error: any) {
    toast.error(
      error?.response?.data?.message ?? "Couldn't request this download.",
    );
    return false;
  } finally {
    setIsDownloadAll(false);
  }
}
